import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  orderRelease,
  orderReturn,
  getOrderById,
  getAllApprovedOrders,
} from '../services/order_service'
import {
  getAllApprovedSamples,
  getSample,
  releaseSample,
  returnSample,
  getSamples,
} from '../services/samples_service'
import { getUserRoles, getUsers } from '../services/user_service'
import { getPrintRequests } from '../services/print_service'
import { createStockMovement } from '../services/stock_service'
import './Stores.css'

const tabs = [
  {
    id: 'sample-release',
    label: 'Sample Release',
    permission: 'releaseSample',
  },
  { id: 'sample-return', label: 'Samples Return', permission: 'returnSample' },
  { id: 'order-release', label: 'Order Release', permission: 'releaseOrder' },
  { id: 'order-return', label: 'Order Return', permission: 'returnOrder' },
  { id: 'print-request', label: 'Print Request', permission: 'addStock' },
]

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOrderId = (order) =>
  getValue(order, ['orderid', 'orderId', 'order_id', 'id'], '')
const getSampleId = (sample) =>
  getValue(sample, ['sampleid', 'sampleId', 'sample_id', 'id'], '')
const getPrintRequestId = (request) =>
  getValue(request, ['print_requestid', 'printRequestId', 'id'], '')
const getUserId = (user) =>
  getValue(user, ['userid', 'userId', 'user_id', 'id'], '')
const getUserName = (user) =>
  getValue(user, ['user_fullname', 'fullname', 'fullName', 'name', 'email'])
const getRoleId = (record) =>
  getValue(record, ['roleid', 'role_id', 'roleId', 'id'], '')
const getUserRoleId = (user) => {
  const role = user?.role
  return typeof role === 'object'
    ? getRoleId(role)
    : getValue(user, ['roleid', 'role_id', 'roleId', 'role'], '')
}
const getUserRoleName = (user) =>
  getValue(
    user,
    ['user_role', 'role_name', 'roleName'],
    typeof user?.role === 'object'
      ? getValue(user.role, ['role_name', 'name'], '')
      : '',
  )
const getBookTitle = (book) => {
  const title = getValue(book, [
    'book_title',
    'bookTitle',
    'book',
    'description',
    'title',
    'name',
  ])
  if (typeof title !== 'object' || title === null) return title
  return getValue(title, [
    'book_title',
    'bookTitle',
    'title',
    'name',
    'description',
  ])
}
const getQuantity = (book) =>
  getValue(book, ['quantity', 'ordered_quantity', 'book_quantity', 'qty'], 0)
const getQuantityValidationError = (books, quantitiesByBook) => {
  const quantityTypes = ['good', 'bad', 'kept']
  const invalidBook = books.find((book, index) => {
    const bookId = getBookId(book, index)
    const quantities = quantitiesByBook[bookId] || {}
    const actualQuantity = Number(getQuantity(book)) || 0
    const enteredQuantity = quantityTypes.reduce(
      (total, type) => total + (Number(quantities[type]) || 0),
      0,
    )
    return enteredQuantity > actualQuantity
  })

  if (!invalidBook) return ''

  return `The returned quantity for ${getBookTitle(invalidBook)} cannot exceed ${getQuantity(invalidBook)}.`
}
const getBookId = (book, fallback) =>
  getValue(book, ['bookid', 'bookId', 'book_id', 'id'], fallback)
const getReturnedQuantity = (book, type) =>
  getValue(book, [type, `${type}_quantity`, `${type}Quantity`], 0)
const getRequesterName = (sample) => {
  const requester = getValue(
    sample,
    ['request_user', 'requesting_user', 'requested_by', 'requester'],
    '-',
  )
  return typeof requester === 'object' ? getUserName(requester) : requester
}
const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}

function Stores() {
  const { user, hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState('order-release')
  const [samples, setSamples] = useState([])
  const [selectedSampleId, setSelectedSampleId] = useState('')
  const [selectedSample, setSelectedSample] = useState(null)
  const [returningSamples, setReturningSamples] = useState([])
  const [returnQuantities, setReturnQuantities] = useState({})
  const [orders, setOrders] = useState([])
  const [salesUsers, setSalesUsers] = useState([])
  const [selectedOrderId, setSelectedOrderId] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [orderReturnId, setOrderReturnId] = useState('')
  const [selectedReturnOrder, setSelectedReturnOrder] = useState(null)
  const [orderReturnQuantities, setOrderReturnQuantities] = useState({})
  const [printRequests, setPrintRequests] = useState([])
  const [selectedPrintRequestId, setSelectedPrintRequestId] = useState('')
  const [selectedPrintRequest, setSelectedPrintRequest] = useState(null)
  const [printedStockComment, setPrintedStockComment] = useState('')
  const [isAddingPrintedStock, setIsAddingPrintedStock] = useState(false)
  const [releasedTo, setReleasedTo] = useState('')
  const [comment, setComment] = useState('')
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [isLoadingOrder, setIsLoadingOrder] = useState(false)
  const [isReleasing, setIsReleasing] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const getSalesUsers = async (signal) => {
    const [userResult, roles] = await Promise.all([
      getUsers({ role: 'sales', limit: 100, signal }),
      getUserRoles(signal),
    ])
    const salesRoleIds = roles
      .filter((role) => normalize(role.role_name) === 'sales')
      .map((role) => String(getRoleId(role)))
    return userResult.users.filter(
      (salesUser) =>
        normalize(getUserRoleName(salesUser)) === 'sales' ||
        salesRoleIds.includes(String(getUserRoleId(salesUser))),
    )
  }

  useEffect(() => {
    if (activeTab !== 'sample-release') return undefined

    const controller = new AbortController()
    setIsLoadingOptions(true)
    setError('')
    Promise.all([getAllApprovedSamples(), getSalesUsers(controller.signal)])
      .then(([approvedSamples, usersWithSalesRole]) => {
        setSamples(approvedSamples)
        setSalesUsers(usersWithSalesRole)
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      })

    return () => controller.abort()
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'sample-return') return undefined

    const controller = new AbortController()
    setIsLoadingOptions(true)
    setError('')
    Promise.all([getSamples(), getSalesUsers(controller.signal)])
      .then(([{ samples: allSamples }, usersWithSalesRole]) => {
        setReturningSamples(
          allSamples.filter((sample) =>
            ['delivered'].includes(
              normalize(getValue(sample, ['status', 'sample_status'])),
            ),
          ),
        )
        setSalesUsers(usersWithSalesRole)
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      })

    return () => controller.abort()
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'order-release') return undefined

    const controller = new AbortController()
    setIsLoadingOptions(true)
    setError('')
    Promise.all([getAllApprovedOrders(), getSalesUsers(controller.signal)])
      .then(([allOrders, usersWithSalesRole]) => {
        const approvedOrders = allOrders.filter((order) =>
          ['approved', 'approve'].includes(
            normalize(getValue(order, ['status', 'order_status'])),
          ),
        )
        setOrders(approvedOrders)
        setSalesUsers(usersWithSalesRole)
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      })

    return () => controller.abort()
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'order-return') return undefined

    const controller = new AbortController()
    setIsLoadingOptions(true)
    setError('')
    getSalesUsers(controller.signal)
      .then((usersWithSalesRole) => setSalesUsers(usersWithSalesRole))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      })

    return () => controller.abort()
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'print-request') return undefined

    const controller = new AbortController()
    setIsLoadingOptions(true)
    setError('')
    setSelectedPrintRequestId('')
    setSelectedPrintRequest(null)
    getPrintRequests({ signal: controller.signal })
      .then(({ requests }) => {
        setPrintRequests(
          requests.filter((request) =>
            ['approved', 'approve'].includes(
              normalize(getValue(request, ['status', 'request_status'])),
            ),
          ),
        )
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      })

    return () => controller.abort()
  }, [activeTab])

  const selectSample = async (event) => {
    const sampleId = event.target.value
    setSelectedSampleId(sampleId)
    setSelectedSample(null)
    setReleasedTo('')
    setComment('')
    setNotice('')
    setError('')
    if (!sampleId) return

    setIsLoadingOrder(true)
    try {
      setSelectedSample(await getSample(sampleId))
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoadingOrder(false)
    }
  }

  const selectReturnSample = async (event) => {
    const sampleId = event.target.value
    setSelectedSampleId(sampleId)
    setSelectedSample(null)
    setReturnQuantities({})
    setComment('')
    setNotice('')
    setError('')
    if (!sampleId) return

    setIsLoadingOrder(true)
    try {
      const sample = await getSample(sampleId)
      const deliveredBooks = Array.isArray(sample.DeliveredBooks)
        ? sample.DeliveredBooks
        : []
      setSelectedSample(sample)
      setReturnQuantities(
        deliveredBooks.reduce((quantities, book, index) => {
          const bookId = getBookId(book, index)
          quantities[bookId] = {
            good: getReturnedQuantity(book, 'good'),
            bad: getReturnedQuantity(book, 'bad'),
            kept: getReturnedQuantity(book, 'kept'),
          }
          return quantities
        }, {}),
      )
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoadingOrder(false)
    }
  }

  const updateReturnQuantity = (bookId, type, value) => {
    setReturnQuantities((current) => ({
      ...current,
      [bookId]: {
        ...current[bookId],
        [type]: value,
      },
    }))
  }

  const submitSampleReturn = async (event) => {
    event.preventDefault()
    if (!selectedSample) return

    const quantityError = getQuantityValidationError(
      deliveredBooks,
      returnQuantities,
    )
    if (quantityError) {
      setError(quantityError)
      return
    }

    setIsReleasing(true)
    setError('')
    setNotice('')
    try {
      await returnSample(getSampleId(selectedSample), {
        returned_books: Object.entries(returnQuantities).map(
          ([bookid, quantities]) => {
            const book = deliveredBooks.find(
              (deliveredBook, index) =>
                String(getBookId(deliveredBook, index)) === String(bookid),
            )
            return {
              ...quantities,
              sampleid: getSampleId(selectedSample),
              sample_bookid: getValue(book, [
                'sample_bookid',
                'sampleBookId',
                'sample_book_id',
              ]),
              bookid,
              good: quantities.good,
              bad: quantities.bad,
              kept: quantities.kept,
            }
          },
        ),
        return_comment: comment.trim(),
        returned_date: new Date().toISOString().slice(0, 10),
        returned_by: releasedTo,
        received_by: getUserId(user),
      })
      setReturningSamples((current) =>
        current.filter(
          (sample) => String(getSampleId(sample)) !== String(selectedSampleId),
        ),
      )
      setNotice(`Sample ${selectedSampleId} has been received.`)
      setSelectedSampleId('')
      setSelectedSample(null)
      setReturnQuantities({})
      setComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsReleasing(false)
    }
  }

  const releaseSampleRequest = async (event) => {
    event.preventDefault()
    if (!selectedSample || !releasedTo) return

    setIsReleasing(true)
    setError('')
    setNotice('')
    try {
      await releaseSample(getSampleId(selectedSample), {
        released_to_userid: releasedTo,
        release_comment: comment.trim(),
        released_date: new Date().toISOString().slice(0, 10),
        userid: getUserId(user),
      })
      setSamples((current) =>
        current.filter(
          (sample) => String(getSampleId(sample)) !== String(selectedSampleId),
        ),
      )
      setNotice(`Sample ${selectedSampleId} has been released.`)
      setSelectedSampleId('')
      setSelectedSample(null)
      setReleasedTo('')
      setComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsReleasing(false)
    }
  }

  const selectOrder = async (event) => {
    const orderId = event.target.value
    setSelectedOrderId(orderId)
    setSelectedOrder(null)
    setReleasedTo('')
    setComment('')
    setNotice('')
    setError('')
    if (!orderId) return

    setIsLoadingOrder(true)
    try {
      const orderResponse = await getOrderById(orderId)
      setSelectedOrder(
        orderResponse?.order ?? orderResponse?.data ?? orderResponse,
      )
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoadingOrder(false)
    }
  }

  const selectPrintRequest = (event) => {
    const requestId = event.target.value
    setSelectedPrintRequestId(requestId)
    setSelectedPrintRequest(
      printRequests.find(
        (request) => String(getPrintRequestId(request)) === String(requestId),
      ) || null,
    )
    setPrintedStockComment('')
    setError('')
    setNotice('')
  }

  const addPrintedBookToStock = async () => {
    if (!selectedPrintRequest) return

    setIsAddingPrintedStock(true)
    setError('')
    setNotice('')
    try {
      await createStockMovement({
        book_type: getValue(
          selectedPrintRequest,
          ['book_type', 'bookType'],
          'published_book',
        ),
        bookid: getValue(selectedPrintRequest, ['bookid', 'book_id', 'bookId']),
        quantity: Number(
          getValue(selectedPrintRequest, ['quantity', 'qty'], 0),
        ),
        direction: 'in',
        reason: printedStockComment.trim(),
        userid: getUserId(user),
        print_requestid: getPrintRequestId(selectedPrintRequest),
      })
      setNotice(
        `${getValue(selectedPrintRequest, [
          'book_title',
          'bookTitle',
          'title',
          'name',
        ])} has been added to stock.`,
      )
      const completedPrintRequestId = getPrintRequestId(selectedPrintRequest)
      setPrintRequests((current) =>
        current.filter(
          (request) =>
            String(getPrintRequestId(request)) !==
            String(completedPrintRequestId),
        ),
      )
      setSelectedPrintRequestId('')
      setSelectedPrintRequest(null)
      setPrintedStockComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsAddingPrintedStock(false)
    }
  }

  const fetchOrderForReturn = async (event) => {
    event.preventDefault()
    const orderId = orderReturnId.trim()
    setSelectedReturnOrder(null)
    setOrderReturnQuantities({})
    setReleasedTo('')
    setComment('')
    setNotice('')
    setError('')
    if (!orderId) return

    setIsLoadingOrder(true)
    try {
      const orderResponse = await getOrderById(orderId)
      const order = orderResponse?.order ?? orderResponse?.data ?? orderResponse
      const orderStatus = normalize(getValue(order, ['status', 'order_status']))
      if (['approved', 'paid', 'requested'].includes(orderStatus)) {
        setError(
          `Order ${orderId} cannot be returned while its status is ${orderStatus}.`,
        )
        return
      }
      const orderBooks = getValue(
        order,
        ['orderBooks', 'order_books', 'books', 'items'],
        [],
      )
      setSelectedReturnOrder(order)
      setOrderReturnQuantities(
        (Array.isArray(orderBooks) ? orderBooks : []).reduce(
          (quantities, book, index) => {
            const bookId = getBookId(book, index)
            quantities[bookId] = {
              good: getReturnedQuantity(book, 'good'),
              bad: getReturnedQuantity(book, 'bad'),
              kept: getReturnedQuantity(book, 'kept'),
            }
            return quantities
          },
          {},
        ),
      )
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoadingOrder(false)
    }
  }

  const updateOrderReturnQuantity = (bookId, type, value) => {
    setOrderReturnQuantities((current) => ({
      ...current,
      [bookId]: { ...current[bookId], [type]: value },
    }))
  }

  const submitOrderReturn = async (event) => {
    event.preventDefault()
    if (!selectedReturnOrder || !releasedTo) return

    const quantityError = getQuantityValidationError(
      returnOrderBooks,
      orderReturnQuantities,
    )
    if (quantityError) {
      setError(quantityError)
      return
    }

    setIsReleasing(true)
    setError('')
    setNotice('')
    try {
      await orderReturn(getOrderId(selectedReturnOrder), {
        status: 'returned',
        returned_books: Object.entries(orderReturnQuantities).map(
          ([bookid, quantities]) => {
            const book = returnOrderBooks.find(
              (orderBook, index) =>
                String(getBookId(orderBook, index)) === String(bookid),
            )
            return {
              bookid,
              quantity: Number(getQuantity(book)),
              order_itemid: getValue(book, ['order_bookid', 'orderItemId']),
              ...quantities,
            }
          },
        ),
        returned_to_userid: releasedTo,
        return_comment: comment.trim(),
        returned_date: new Date().toISOString().slice(0, 10),
        userid: getUserId(user),
      })
      setNotice(`Order ${getOrderId(selectedReturnOrder)} has been received.`)
      setOrderReturnId('')
      setSelectedReturnOrder(null)
      setOrderReturnQuantities({})
      setReleasedTo('')
      setComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsReleasing(false)
    }
  }

  const releaseOrder = async (event) => {
    event.preventDefault()
    if (!selectedOrder || !releasedTo) return

    setIsReleasing(true)
    setError('')
    setNotice('')
    try {
      await orderRelease(getOrderId(selectedOrder), {
        released_to_userid: releasedTo,
        release_comment: comment.trim(),
        released_date: new Date().toISOString().slice(0, 10),
        userid: getUserId(user),
      })
      setOrders((current) =>
        current.filter(
          (order) => String(getOrderId(order)) !== String(selectedOrderId),
        ),
      )
      setNotice(`Order ${selectedOrderId} has been released.`)
      setSelectedOrderId('')
      setSelectedOrder(null)
      setReleasedTo('')
      setComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsReleasing(false)
    }
  }

  const books = getValue(
    selectedOrder,
    ['orderBooks', 'order_books', 'books', 'items'],
    [],
  )
  const sampleBooks = getValue(
    selectedSample,
    [
      'books',
      'sample_books',
      'sampleBooks',
      'requested_books',
      'requestedBooks',
      'bookList',
      'DeliveredBooks',
      'deliveredBooks',
    ],
    [],
  )
  const deliveredBooks = Array.isArray(selectedSample?.DeliveredBooks)
    ? selectedSample.DeliveredBooks
    : []
  const returnOrderBooks = getValue(
    selectedReturnOrder,
    ['orderBooks', 'order_books', 'books', 'items'],
    [],
  )

  return (
    <div className='stores-page'>
      <nav className='stores-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span>/</span>
        <strong>Stores</strong>
      </nav>

      <header className='stores-page__header'>
        <span className='app-content__eyebrow'>Inventory operations</span>
        <h1>Stores</h1>
        <p>Release approved requests and record who receives each order.</p>
      </header>

      <section className='stores-workspace'>
        <div className='stores-tabs' role='tablist' aria-label='Stores actions'>
          {tabs
            .filter((tab) => hasPermission(tab.permission))
            .map((tab) => (
              <button
                key={tab.id}
                className={activeTab === tab.id ? 'is-active' : ''}
                type='button'
                role='tab'
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
        </div>

        {activeTab === 'sample-release' ? (
          <div className='stores-workspace__body'>
            <label className='stores-order-select'>
              <span>Approved sample</span>
              <select
                value={selectedSampleId}
                onChange={selectSample}
                disabled={isLoadingOptions || isReleasing}
              >
                <option value=''>
                  {isLoadingOptions
                    ? 'Loading approved samples...'
                    : '-- Select sample to release --'}
                </option>
                {samples.map((sample) => (
                  <option key={getSampleId(sample)} value={getSampleId(sample)}>
                    Sample {getSampleId(sample)}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <div
                className='stores-message stores-message--error'
                role='alert'
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                className='stores-message stores-message--success'
                role='status'
              >
                {notice}
              </div>
            )}
            {isLoadingOrder && (
              <div className='stores-loading'>Loading sample details...</div>
            )}

            {selectedSample && !isLoadingOrder && (
              <form
                className='stores-release-form'
                onSubmit={releaseSampleRequest}
              >
                <section className='stores-card stores-card--details'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Release request</span>
                      <h2>Sample {getSampleId(selectedSample)}</h2>
                    </div>
                    <strong className='stores-status'>
                      {getValue(
                        selectedSample,
                        ['status', 'sample_status'],
                        'Approved',
                      )}
                    </strong>
                  </div>
                  <dl className='stores-details'>
                    <div>
                      <dt>Sample ID</dt>
                      <dd>{getSampleId(selectedSample)}</dd>
                    </div>
                    <div>
                      <dt>Requested by</dt>
                      <dd>{getRequesterName(selectedSample)}</dd>
                    </div>
                    <div>
                      <dt>Request date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedSample, [
                            'request_date',
                            'requested_at',
                            'created_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved by</dt>
                      <dd>
                        {getValue(selectedSample, [
                          'approved_user',
                          'approver',
                          'approve_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedSample, [
                            'approve_date',
                            'approved_at',
                            'approval_date',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        {getValue(
                          selectedSample,
                          ['status', 'sample_status'],
                          'Approved',
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className='stores-card'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Sample contents</span>
                      <h2>Books in sample</h2>
                    </div>
                    <strong>
                      {Array.isArray(sampleBooks) ? sampleBooks.length : 0}{' '}
                      titles
                    </strong>
                  </div>
                  <div className='stores-books'>
                    <div className='stores-books__header'>
                      <span>Book title</span>
                      <span>Quantity</span>
                    </div>
                    {Array.isArray(sampleBooks) && sampleBooks.length ? (
                      sampleBooks.map((book, index) => (
                        <div
                          className='stores-books__row'
                          key={getValue(
                            book,
                            ['bookid', 'bookId', 'book_id', 'id'],
                            index,
                          )}
                        >
                          <strong>{getBookTitle(book)}</strong>
                          <span>{getQuantity(book)}</span>
                        </div>
                      ))
                    ) : (
                      <p className='stores-books__empty'>
                        No books were returned for this sample.
                      </p>
                    )}
                  </div>
                </section>

                <section className='stores-card stores-card--release'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Handover</span>
                      <h2>Release info</h2>
                    </div>
                  </div>
                  <label>
                    Released to
                    <select
                      value={releasedTo}
                      onChange={(event) => setReleasedTo(event.target.value)}
                      required
                    >
                      <option value=''>Select a sales user</option>
                      {salesUsers.map((salesUser) => (
                        <option
                          key={getUserId(salesUser)}
                          value={getUserId(salesUser)}
                        >
                          {getUserName(salesUser)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Release comment
                    <textarea
                      rows='5'
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder='Add handover notes or condition details'
                    />
                  </label>
                  <button
                    className='stores-release-button'
                    type='submit'
                    disabled={isReleasing || !releasedTo}
                  >
                    {isReleasing ? 'Releasing sample...' : 'Release sample'}
                  </button>
                </section>
              </form>
            )}
          </div>
        ) : activeTab === 'sample-return' ? (
          <div className='stores-workspace__body'>
            <label className='stores-order-select'>
              <span>Sample to receive</span>
              <select
                value={selectedSampleId}
                onChange={selectReturnSample}
                disabled={isLoadingOptions || isReleasing}
              >
                <option value=''>
                  {isLoadingOptions
                    ? 'Loading samples...'
                    : '-- Select sample to receive --'}
                </option>
                {returningSamples.map((sample) => (
                  <option key={getSampleId(sample)} value={getSampleId(sample)}>
                    Sample {getSampleId(sample)}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <div
                className='stores-message stores-message--error'
                role='alert'
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                className='stores-message stores-message--success'
                role='status'
              >
                {notice}
              </div>
            )}
            {isLoadingOrder && (
              <div className='stores-loading'>Loading sample details...</div>
            )}

            {selectedSample && !isLoadingOrder && (
              <form
                className='stores-release-form stores-return-form'
                onSubmit={submitSampleReturn}
              >
                <section className='stores-card stores-card--details'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Sample request details</span>
                      <h2>Sample {getSampleId(selectedSample)}</h2>
                    </div>
                    <strong className='stores-status'>
                      {getValue(
                        selectedSample,
                        ['status', 'sample_status'],
                        'Delivered',
                      )}
                    </strong>
                  </div>
                  <dl className='stores-details'>
                    <div>
                      <dt>Request ID</dt>
                      <dd>{getSampleId(selectedSample)}</dd>
                    </div>
                    <div>
                      <dt>Request by</dt>
                      <dd>{getRequesterName(selectedSample)}</dd>
                    </div>
                    <div>
                      <dt>Request date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedSample, [
                            'request_date',
                            'requested_at',
                            'created_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved by</dt>
                      <dd>
                        {getValue(selectedSample, [
                          'approved_user',
                          'approver',
                          'approve_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedSample, [
                            'approve_date',
                            'approved_at',
                            'approval_date',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Delivery date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedSample, [
                            'release_date',
                            'delivery_date',
                            'released_at',
                          ]),
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className='stores-card stores-card--return-books'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Returning books</span>
                      <h2>Record returned condition</h2>
                    </div>
                    <strong>{deliveredBooks.length} titles</strong>
                  </div>
                  <div className='stores-books stores-return-books'>
                    <div className='stores-books__header'>
                      <span>Title</span>
                      <span>Qty</span>
                      <span>Good</span>
                      <span>Bad</span>
                      <span>Kept</span>
                    </div>
                    {deliveredBooks.length ? (
                      deliveredBooks.map((book, index) => {
                        const bookId = getBookId(book, index)
                        const quantities = returnQuantities[bookId] || {}
                        return (
                          <div className='stores-books__row' key={bookId}>
                            <strong>{getBookTitle(book)}</strong>
                            <span>{getQuantity(book)}</span>
                            {['good', 'bad', 'kept'].map((type) => (
                              <input
                                key={type}
                                aria-label={`${type} quantity for ${getBookTitle(book)}`}
                                type='number'
                                min='0'
                                max={getQuantity(book)}
                                value={quantities[type] ?? 0}
                                onChange={(event) =>
                                  updateReturnQuantity(
                                    bookId,
                                    type,
                                    event.target.value,
                                  )
                                }
                                required
                              />
                            ))}
                          </div>
                        )
                      })
                    ) : (
                      <p className='stores-books__empty'>
                        No delivered books were found for this sample.
                      </p>
                    )}
                  </div>
                </section>

                <section className='stores-card stores-card--release'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Return handover</span>
                      <h2>Receive sample</h2>
                    </div>
                  </div>
                  <label>
                    Returned by
                    <select
                      value={releasedTo}
                      onChange={(event) => setReleasedTo(event.target.value)}
                      required
                    >
                      <option value=''>-- Select user --</option>
                      {salesUsers.map((salesUser) => (
                        <option
                          key={getUserId(salesUser)}
                          value={getUserId(salesUser)}
                        >
                          {getUserName(salesUser)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Comments (optional)
                    <textarea
                      rows='5'
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder='Optional'
                    />
                  </label>
                  <button
                    className='stores-release-button'
                    type='submit'
                    disabled={isReleasing || !releasedTo}
                  >
                    {isReleasing ? 'Receiving sample...' : 'Receive'}
                  </button>
                </section>
              </form>
            )}
          </div>
        ) : activeTab === 'print-request' ? (
          <div className='stores-workspace__body'>
            <label className='stores-order-select'>
              <span>Approved print request</span>
              <select
                value={selectedPrintRequestId}
                onChange={selectPrintRequest}
                disabled={isLoadingOptions}
              >
                <option value=''>
                  {isLoadingOptions
                    ? 'Loading approved print requests...'
                    : '-- Select print request --'}
                </option>
                {printRequests.map((request) => (
                  <option
                    key={getPrintRequestId(request)}
                    value={getPrintRequestId(request)}
                  >
                    Request {getPrintRequestId(request)}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <div
                className='stores-message stores-message--error'
                role='alert'
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                className='stores-message stores-message--success'
                role='status'
              >
                {notice}
              </div>
            )}

            {selectedPrintRequest && (
              <div className='stores-release-form stores-print-request-form'>
                <section className='stores-card stores-card--details'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Print request details</span>
                      <h2>Request {getPrintRequestId(selectedPrintRequest)}</h2>
                    </div>
                    <strong className='stores-status'>
                      {getValue(
                        selectedPrintRequest,
                        ['status', 'request_status'],
                        'Approved',
                      )}
                    </strong>
                  </div>
                  <dl className='stores-details'>
                    <div>
                      <dt>Request ID</dt>
                      <dd>{getPrintRequestId(selectedPrintRequest)}</dd>
                    </div>
                    <div>
                      <dt>Requested by</dt>
                      <dd>
                        {getValue(selectedPrintRequest, [
                          'requesting_user',
                          'requester',
                          'user_fullname',
                          'created_by',
                          'userid',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Request date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedPrintRequest, [
                            'request_date',
                            'requested_at',
                            'created_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved by</dt>
                      <dd>
                        {getValue(selectedPrintRequest, [
                          'approving_user',
                          'approver',
                          'approved_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedPrintRequest, [
                            'approved_date',
                            'approve_date',
                            'approved_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        {getValue(
                          selectedPrintRequest,
                          ['status', 'request_status'],
                          'Approved',
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className='stores-card'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Printing details</span>
                      <h2>
                        {getValue(selectedPrintRequest, [
                          'book_title',
                          'bookTitle',
                          'title',
                          'name',
                        ])}
                      </h2>
                    </div>
                    <strong>
                      {getValue(selectedPrintRequest, ['quantity', 'qty'], 0)}{' '}
                      copies
                    </strong>
                  </div>
                  <dl className='stores-details stores-details--single'>
                    <div>
                      <dt>Book ID</dt>
                      <dd>
                        {getValue(selectedPrintRequest, [
                          'bookid',
                          'book_id',
                          'bookId',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Quantity</dt>
                      <dd>
                        {getValue(selectedPrintRequest, ['quantity', 'qty'], 0)}
                      </dd>
                    </div>
                    <div>
                      <dt>Comment</dt>
                      <dd>
                        {getValue(selectedPrintRequest, [
                          'requesting_comment',
                          'comment',
                          'remarks',
                        ])}
                      </dd>
                    </div>
                  </dl>
                </section>
                <section className='stores-card stores-card--stock-action'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Inventory action</span>
                      <h2>Add to stock</h2>
                    </div>
                  </div>
                  <p className='stores-stock-action__copy'>
                    Record the approved printed quantity as incoming stock.
                  </p>
                  <label className='stores-stock-action__label'>
                    Stock comment (optional)
                    <textarea
                      rows='4'
                      value={printedStockComment}
                      onChange={(event) =>
                        setPrintedStockComment(event.target.value)
                      }
                      placeholder='Add a note about this stock entry'
                    />
                  </label>
                  <button
                    className='stores-release-button stores-stock-button'
                    type='button'
                    onClick={addPrintedBookToStock}
                    disabled={isAddingPrintedStock}
                  >
                    {isAddingPrintedStock
                      ? 'Adding printed books...'
                      : 'Add Printed Books to Stock'}
                  </button>
                </section>
              </div>
            )}
          </div>
        ) : activeTab === 'order-return' ? (
          <div className='stores-workspace__body'>
            <form className='stores-order-fetch' onSubmit={fetchOrderForReturn}>
              <label className='stores-order-select'>
                <span>Order ID</span>
                <input
                  type='number'
                  min='1'
                  value={orderReturnId}
                  onChange={(event) => setOrderReturnId(event.target.value)}
                  placeholder='Enter order ID'
                  disabled={isLoadingOrder || isReleasing}
                  required
                />
              </label>
              <button
                className='stores-fetch-button'
                type='submit'
                disabled={isLoadingOrder || isReleasing || !orderReturnId}
              >
                {isLoadingOrder ? 'Fetching...' : 'Find'}
              </button>
            </form>

            {error && (
              <div
                className='stores-message stores-message--error'
                role='alert'
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                className='stores-message stores-message--success'
                role='status'
              >
                {notice}
              </div>
            )}

            {selectedReturnOrder && !isLoadingOrder && (
              <form
                className='stores-release-form stores-return-form'
                onSubmit={submitOrderReturn}
              >
                <section className='stores-card stores-card--details'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Order request details</span>
                      <h2>Order {getOrderId(selectedReturnOrder)}</h2>
                    </div>
                    <strong className='stores-status'>
                      {getValue(
                        selectedReturnOrder,
                        ['status', 'order_status'],
                        'Delivered',
                      )}
                    </strong>
                  </div>
                  <dl className='stores-details'>
                    <div>
                      <dt>Request ID</dt>
                      <dd>{getOrderId(selectedReturnOrder)}</dd>
                    </div>
                    <div>
                      <dt>Request by</dt>
                      <dd>
                        {getValue(selectedReturnOrder, [
                          'requesting_user',
                          'requester',
                          'user_fullname',
                          'created_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Request date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedReturnOrder, [
                            'request_date',
                            'order_date',
                            'created_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved by</dt>
                      <dd>
                        {getValue(selectedReturnOrder, [
                          'approving_user',
                          'approver',
                          'approved_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedReturnOrder, [
                            'approved_date',
                            'approve_date',
                            'approved_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Delivery date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedReturnOrder, [
                            'released_date',
                            'delivery_date',
                            'released_at',
                          ]),
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className='stores-card stores-card--return-books'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Returning books</span>
                      <h2>Record returned condition</h2>
                    </div>
                    <strong>
                      {Array.isArray(returnOrderBooks)
                        ? returnOrderBooks.length
                        : 0}{' '}
                      titles
                    </strong>
                  </div>
                  <div className='stores-books stores-return-books'>
                    <div className='stores-books__header'>
                      <span>Title</span>
                      <span>Qty</span>
                      <span>Good</span>
                      <span>Bad</span>
                      <span>Missing</span>
                    </div>
                    {Array.isArray(returnOrderBooks) &&
                    returnOrderBooks.length ? (
                      returnOrderBooks.map((book, index) => {
                        const bookId = getBookId(book, index)
                        const quantities = orderReturnQuantities[bookId] || {}
                        return (
                          <div className='stores-books__row' key={bookId}>
                            <strong>{getBookTitle(book)}</strong>
                            <span>{getQuantity(book)}</span>
                            {['good', 'bad', 'kept'].map((type) => (
                              <input
                                key={type}
                                type='number'
                                min='0'
                                max={getQuantity(book)}
                                value={quantities[type] ?? 0}
                                aria-label={`${type} quantity for ${getBookTitle(book)}`}
                                onChange={(event) =>
                                  updateOrderReturnQuantity(
                                    bookId,
                                    type,
                                    event.target.value,
                                  )
                                }
                                required
                              />
                            ))}
                          </div>
                        )
                      })
                    ) : (
                      <p className='stores-books__empty'>
                        No books were found for this order.
                      </p>
                    )}
                  </div>
                </section>

                <section className='stores-card stores-card--release'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Return handover</span>
                      <h2>Receive order</h2>
                    </div>
                  </div>
                  <label>
                    Received by
                    <select
                      value={releasedTo}
                      onChange={(event) => setReleasedTo(event.target.value)}
                      required
                    >
                      <option value=''>-- Select user --</option>
                      {salesUsers.map((salesUser) => (
                        <option
                          key={getUserId(salesUser)}
                          value={getUserId(salesUser)}
                        >
                          {getUserName(salesUser)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Comments (optional)
                    <textarea
                      rows='5'
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder='Optional'
                    />
                  </label>
                  <button
                    className='stores-release-button'
                    type='submit'
                    disabled={isReleasing || !releasedTo}
                  >
                    {isReleasing ? 'Receiving order...' : 'Receive'}
                  </button>
                </section>
              </form>
            )}
          </div>
        ) : activeTab === 'order-release' ? (
          <div className='stores-workspace__body'>
            <label className='stores-order-select'>
              <span>Approved order</span>
              <select
                value={selectedOrderId}
                onChange={selectOrder}
                disabled={isLoadingOptions || isReleasing}
              >
                <option value=''>
                  {isLoadingOptions
                    ? 'Loading approved orders...'
                    : '-- Select order to release --'}
                </option>
                {orders.map((order) => (
                  <option key={getOrderId(order)} value={getOrderId(order)}>
                    Order {getOrderId(order)}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <div
                className='stores-message stores-message--error'
                role='alert'
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                className='stores-message stores-message--success'
                role='status'
              >
                {notice}
              </div>
            )}
            {isLoadingOrder && (
              <div className='stores-loading'>Loading order details...</div>
            )}

            {selectedOrder && !isLoadingOrder && (
              <form className='stores-release-form' onSubmit={releaseOrder}>
                <section className='stores-card stores-card--details'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Release request</span>
                      <h2>Order {getOrderId(selectedOrder)}</h2>
                    </div>
                    <strong className='stores-status'>
                      {getValue(
                        selectedOrder,
                        ['status', 'order_status'],
                        'Approved',
                      )}
                    </strong>
                  </div>
                  <dl className='stores-details'>
                    <div>
                      <dt>Order ID</dt>
                      <dd>{getOrderId(selectedOrder)}</dd>
                    </div>
                    <div>
                      <dt>Requested by</dt>
                      <dd>
                        {getValue(selectedOrder, [
                          'requesting_user',
                          'requester',
                          'user_fullname',
                          'created_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Request date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedOrder, [
                            'request_date',
                            'order_date',
                            'created_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved by</dt>
                      <dd>
                        {getValue(selectedOrder, [
                          'approving_user',
                          'approver',
                          'approved_by',
                        ])}
                      </dd>
                    </div>
                    <div>
                      <dt>Approved date</dt>
                      <dd>
                        {formatDate(
                          getValue(selectedOrder, [
                            'approved_date',
                            'approve_date',
                            'approved_at',
                          ]),
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        {getValue(
                          selectedOrder,
                          ['status', 'order_status'],
                          'Approved',
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className='stores-card'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Order contents</span>
                      <h2>Books in order</h2>
                    </div>
                    <strong>
                      {Array.isArray(books) ? books.length : 0} titles
                    </strong>
                  </div>
                  <div className='stores-books'>
                    <div className='stores-books__header'>
                      <span>Book title</span>
                      <span>Quantity</span>
                    </div>
                    {Array.isArray(books) && books.length ? (
                      books.map((book, index) => (
                        <div
                          className='stores-books__row'
                          key={getValue(
                            book,
                            ['bookid', 'book_id', 'id'],
                            index,
                          )}
                        >
                          <strong>{getBookTitle(book)}</strong>
                          <span>{getQuantity(book)}</span>
                        </div>
                      ))
                    ) : (
                      <p className='stores-books__empty'>
                        No books were returned for this order.
                      </p>
                    )}
                  </div>
                </section>

                <section className='stores-card stores-card--release'>
                  <div className='stores-card__heading'>
                    <div>
                      <span>Handover</span>
                      <h2>Release info</h2>
                    </div>
                  </div>
                  <label>
                    Released to
                    <select
                      value={releasedTo}
                      onChange={(event) => setReleasedTo(event.target.value)}
                      required
                    >
                      <option value=''>Select a sales user</option>
                      {salesUsers.map((salesUser) => (
                        <option
                          key={getUserId(salesUser)}
                          value={getUserId(salesUser)}
                        >
                          {getUserName(salesUser)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Release comment
                    <textarea
                      rows='5'
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder='Add handover notes or condition details'
                    />
                  </label>
                  <button
                    className='stores-release-button'
                    type='submit'
                    disabled={isReleasing || !releasedTo}
                  >
                    {isReleasing ? 'Releasing order...' : 'Release order'}
                  </button>
                </section>
              </form>
            )}
          </div>
        ) : (
          <div className='stores-workspace__empty'>
            <strong>{tabs.find((tab) => tab.id === activeTab)?.label}</strong>
            <span>Select Order Release to process an approved order.</span>
          </div>
        )}
      </section>
    </div>
  )
}

export default Stores
